import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminListRules, adminCreateRule, adminUpdateRule, adminDeleteRule, adminRunRule } from "@/lib/api";
import { Plus, Loader2, Save, X, Trash2, Zap, Send } from "lucide-react";

const KINDS = [
  { key: "scheduled", label: "Scheduled (one-off)", help: "Send once at a chosen date & time." },
  { key: "on_publish", label: "On new content", help: "Auto-send when content is published. Use {title} in the message." },
  { key: "festival_reminder", label: "Festival / Vrat reminder", help: "Send N days before a festival date. Use {festival} and {date}." },
];
const blank = {
  name: "", kind: "scheduled", enabled: true, title: "", body: "", link: "", image_url: "",
  channel_bell: true, channel_strip: false, target_all: true, interests: "", regions: "", deities: "", goal_contains: "",
  scheduled_at: "", content_type: "", days_before: 1,
};
const field = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";
const csv = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

export default function NotificationRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // "new" | rule
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  const load = () => { setLoading(true); adminListRules().then(setRules).catch(() => setRules([])).finally(() => setLoading(false)); };
  useEffect(load, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openEdit = (r) => {
    setForm({ ...blank, ...r, interests: (r.interests || []).join(", "), regions: (r.regions || []).join(", "), deities: (r.deities || []).join(", "), scheduled_at: (r.scheduled_at || "").slice(0, 16) });
    setEditing(r);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("A message title is required"); return; }
    setBusy(true);
    const payload = {
      ...form, interests: csv(form.interests), regions: csv(form.regions), deities: csv(form.deities),
      goal_contains: form.goal_contains.trim() || null, days_before: Number(form.days_before) || 1,
      scheduled_at: form.kind === "scheduled" && form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
    };
    try {
      if (editing === "new") await adminCreateRule(payload);
      else await adminUpdateRule(editing.id, payload);
      toast.success("Rule saved"); setEditing(null); load();
    } catch (err) { toast.error("Could not save", { description: err.response?.data?.detail || err.message }); }
    finally { setBusy(false); }
  };

  const del = async (r) => { if (!window.confirm("Delete this rule?")) return; await adminDeleteRule(r.id); setRules((x) => x.filter((y) => y.id !== r.id)); };
  const toggle = async (r) => { const u = await adminUpdateRule(r.id, { enabled: !r.enabled }); setRules((x) => x.map((y) => y.id === r.id ? u : y)); };
  const runNow = async (r) => { try { const res = await adminRunRule(r.id); toast.success(`Sent to ${res.bell} sadhak${res.bell === 1 ? "" : "s"}${res.strip ? " + homepage strip" : ""}`); load(); } catch (e) { toast.error("Could not send", { description: e.response?.data?.detail || e.message }); } };

  return (
    <div className="mt-10 border border-line bg-surface p-5" data-testid="notification-rules">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="inline-flex items-center gap-2 font-serif-latin text-xl text-ink"><Zap className="h-4 w-4 text-saffron" /> Automated & scheduled rules</h2>
          <p className="mt-1 text-sm text-ink-3">Trigger notifications automatically or at a set time — delivered to the bell and/or homepage strip.</p>
        </div>
        <button onClick={() => { setForm(blank); setEditing("new"); }} data-testid="rule-new" className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white"><Plus className="h-4 w-4" /> New rule</button>
      </div>

      {editing && (
        <div className="mt-5 border border-saffron/40 bg-[rgba(199,80,0,0.03)] p-4" data-testid="rule-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Rule name (internal)" className={field} data-testid="rule-name" />
            <select value={form.kind} onChange={(e) => set("kind", e.target.value)} className={field} data-testid="rule-kind">
              {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
            </select>
          </div>
          <p className="mt-1 text-xs text-ink-3">{KINDS.find((k) => k.key === form.kind)?.help}</p>

          <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Message title" className={`${field} mt-3`} data-testid="rule-title" />
          <textarea value={form.body} onChange={(e) => set("body", e.target.value)} rows={2} placeholder="Body (shown on homepage strip)" className={`${field} mt-3`} />
          <input value={form.link} onChange={(e) => set("link", e.target.value)} placeholder="Link (optional, e.g. /festivals)" className={`${field} mt-3`} />

          {form.kind === "scheduled" && (
            <input type="datetime-local" value={form.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)} className={`${field} mt-3`} data-testid="rule-scheduled-at" />
          )}
          {form.kind === "on_publish" && (
            <input value={form.content_type} onChange={(e) => set("content_type", e.target.value)} placeholder="Content type filter (blank = any): katha, mantra, stotra…" className={`${field} mt-3`} data-testid="rule-content-type" />
          )}
          {form.kind === "festival_reminder" && (
            <label className="mt-3 flex items-center gap-2 text-sm text-ink-2">Days before:
              <input type="number" min={0} max={30} value={form.days_before} onChange={(e) => set("days_before", e.target.value)} className="w-20 border border-line bg-parchment px-2 py-1.5 text-sm" data-testid="rule-days-before" />
            </label>
          )}

          <div className="mt-3 flex flex-wrap gap-4 text-sm text-ink-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.channel_bell} onChange={(e) => set("channel_bell", e.target.checked)} data-testid="rule-bell" /> In-app bell</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.channel_strip} onChange={(e) => set("channel_strip", e.target.checked)} data-testid="rule-strip" /> Homepage strip</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} /> Enabled</label>
          </div>

          <label className="mt-3 flex items-center gap-2 text-sm text-ink-2"><input type="checkbox" checked={form.target_all} onChange={(e) => set("target_all", e.target.checked)} data-testid="rule-target-all" /> Send to everyone</label>
          {!form.target_all && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <input value={form.interests} onChange={(e) => set("interests", e.target.value)} placeholder="Interests (comma sep)" className={field} />
              <input value={form.regions} onChange={(e) => set("regions", e.target.value)} placeholder="Cities (comma sep)" className={field} />
              <input value={form.deities} onChange={(e) => set("deities", e.target.value)} placeholder="Deities (comma sep)" className={field} />
              <input value={form.goal_contains} onChange={(e) => set("goal_contains", e.target.value)} placeholder="Goal contains (text)" className={field} />
            </div>
          )}

          <div className="mt-4 flex gap-2">
            <button onClick={save} disabled={busy} data-testid="rule-save" className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save rule</button>
            <button onClick={() => setEditing(null)} className="border border-line px-4 py-2 text-sm text-ink-2"><X className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      <div className="mt-5 divide-y divide-line border-y border-line">
        {loading ? <div className="flex items-center gap-2 py-8 text-ink-3"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          : rules.length === 0 ? <p className="py-8 text-center text-ink-3">No rules yet.</p>
          : rules.map((r) => (
            <div key={r.id} data-testid={`rule-${r.id}`} className={`flex items-center gap-3 py-3 ${r.enabled ? "" : "opacity-50"}`}>
              <span className="rounded-full bg-surface-alt px-2 py-0.5 text-[10px] uppercase tracking-wide text-ink-3">{KINDS.find((k) => k.key === r.kind)?.label || r.kind}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-serif-latin text-ink">{r.title || r.name || "(untitled)"}</p>
                <p className="truncate text-xs text-ink-3">{[r.channel_bell && "bell", r.channel_strip && "strip"].filter(Boolean).join(" + ")}{r.target_all ? " · everyone" : " · targeted"}{r.scheduled_at ? ` · ${new Date(r.scheduled_at).toLocaleString()}` : ""}{r.kind === "festival_reminder" ? ` · ${r.days_before}d before` : ""}</p>
              </div>
              <button onClick={() => runNow(r)} title="Send now" className="p-2 text-ink-2 hover:text-saffron" data-testid={`rule-run-${r.id}`}><Send className="h-4 w-4" /></button>
              <button onClick={() => toggle(r)} className="p-2 text-ink-2 hover:text-saffron">{r.enabled ? "On" : "Off"}</button>
              <button onClick={() => openEdit(r)} className="p-2 text-ink-2 hover:text-saffron"><Save className="h-4 w-4" /></button>
              <button onClick={() => del(r)} className="p-2 text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
      </div>
    </div>
  );
}
